// Admin — Manual Invoice generator.
// Lists all manually-created invoices and provides a form to raise a new
// one for services rendered outside the website booking flow.
import { createFileRoute, Link, Outlet, useChildMatches } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, FileText, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  adminListManualInvoices,
  adminCreateManualInvoice,
  adminEmailManualInvoice,
} from "@/lib/api/manual-invoices.functions";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/manual-invoices",
)({
  head: () => ({ meta: [{ title: "Admin · Invoice generator" }] }),
  component: ManualInvoicesPage,
});

type LineItem = { description: string; quantity: number; unit_price_cents: number };

const rZAR = (c: number) => `R${(c / 100).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function ManualInvoicesPage() {
  // This route has a child route (`/$id`) — render it instead of the list
  // when the detail page is open, otherwise "Open" appears to do nothing.
  const childMatches = useChildMatches();
  if (childMatches.length > 0) return <Outlet />;
  return <ManualInvoicesList />;
}

function ManualInvoicesList() {
  const listFn = useServerFn(adminListManualInvoices);
  const createFn = useServerFn(adminCreateManualInvoice);
  const emailFn = useServerFn(adminEmailManualInvoice);
  const qc = useQueryClient();

  const [sendingId, setSendingId] = useState<string | null>(null);
  const emailMut = useMutation({
    mutationFn: async (row: any) => {
      if (!row.client_email) throw new Error("This invoice has no client email — open it to send to another address.");
      setSendingId(row.id);
      return await emailFn({ data: { id: row.id } });
    },
    onSuccess: (r: any) => {
      if (r?.ok) toast.success(`Emailed to ${r.sentTo}`);
      else toast.error(r?.reason || "Send failed");
      qc.invalidateQueries({ queryKey: ["admin-manual-invoices"] });
    },
    onError: (e: Error) => toast.error(e.message),
    onSettled: () => setSendingId(null),
  });

  const { data: rows, isLoading } = useQuery({
    queryKey: ["admin-manual-invoices"],
    queryFn: () => listFn({ data: { status: "all", limit: 100 } }),
  });


  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    client_address: "",
    client_registration: "",
    client_vat_number: "",
    notes: "",
    due_date: "",
    charge_vat: false,
    vat_inclusive: false,
  });
  const [items, setItems] = useState<LineItem[]>([
    { description: "", quantity: 1, unit_price_cents: 0 },
  ]);

  const subtotal = items.reduce(
    (s, i) => s + Math.round(i.quantity * i.unit_price_cents),
    0,
  );
  const vat = form.charge_vat
    ? form.vat_inclusive
      ? Math.round((subtotal * 0.15) / 1.15)
      : Math.round(subtotal * 0.15)
    : 0;
  const total = form.charge_vat && !form.vat_inclusive ? subtotal + vat : subtotal;

  const create = useMutation({
    mutationFn: async () => {
      const clean = items.filter((i) => i.description.trim() && i.unit_price_cents >= 0);
      if (clean.length === 0) throw new Error("Add at least one line item");
      return await createFn({
        data: {
          ...form,
          line_items: clean,
        } as any,
      });
    },
    onSuccess: (res) => {
      toast.success(`Invoice ${res.invoice.reference} created`);
      qc.invalidateQueries({ queryKey: ["admin-manual-invoices"] });
      setShowForm(false);
      setForm({
        client_name: "",
        client_email: "",
        client_phone: "",
        client_address: "",
        client_registration: "",
        client_vat_number: "",
        notes: "",
        due_date: "",
        charge_vat: false,
        vat_inclusive: false,
      });
      setItems([{ description: "", quantity: 1, unit_price_cents: 0 }]);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-navy">Invoice generator</h1>
          <p className="text-sm text-muted-foreground">
            Raise invoices for walk-in clients, phone bookings, corporate
            contracts and any services rendered outside the website.
          </p>
        </div>
        <Button onClick={() => setShowForm((v) => !v)}>
          <Plus className="mr-2 h-4 w-4" />
          {showForm ? "Cancel" : "New invoice"}
        </Button>
      </header>

      {showForm && (
        <section className="rounded-lg border border-border bg-card p-5 space-y-4">
          <h2 className="font-display text-lg text-navy">Client details</h2>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label>Client / organisation name *</Label>
              <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} />
            </div>
            <div>
              <Label>Due date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Address</Label>
              <Input value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} />
            </div>
            <div>
              <Label>Company reg no.</Label>
              <Input value={form.client_registration} onChange={(e) => setForm({ ...form, client_registration: e.target.value })} />
            </div>
            <div>
              <Label>VAT no.</Label>
              <Input value={form.client_vat_number} onChange={(e) => setForm({ ...form, client_vat_number: e.target.value })} />
            </div>
          </div>

          <div>
            <h2 className="font-display text-lg text-navy">Line items</h2>
            <div className="mt-2 space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-6">
                    <Label className="text-xs">Description</Label>
                    <Input value={it.description} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Qty</Label>
                    <Input type="number" step="0.01" value={it.quantity} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, quantity: parseFloat(e.target.value) || 0 } : x))} />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Unit price (R)</Label>
                    <Input type="number" step="0.01" value={(it.unit_price_cents / 100).toString()} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, unit_price_cents: Math.round((parseFloat(e.target.value) || 0) * 100) } : x))} />
                  </div>
                  <div className="col-span-1">
                    <Button variant="ghost" size="icon" onClick={() => setItems(items.filter((_, i) => i !== idx))} disabled={items.length === 1}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => setItems([...items, { description: "", quantity: 1, unit_price_cents: 0 }])}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Add line
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.charge_vat} onChange={(e) => setForm({ ...form, charge_vat: e.target.checked })} />
              Charge VAT (15%)
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" disabled={!form.charge_vat} checked={form.vat_inclusive} onChange={(e) => setForm({ ...form, vat_inclusive: e.target.checked })} />
              Prices are VAT-inclusive
            </label>
          </div>

          <div>
            <Label>Notes / description of service</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>

          <div className="rounded-md bg-secondary/40 p-3 text-sm space-y-1">
            <div className="flex justify-between"><span>Subtotal</span><span>{rZAR(form.charge_vat && form.vat_inclusive ? subtotal - vat : subtotal)}</span></div>
            {form.charge_vat && <div className="flex justify-between"><span>VAT (15%)</span><span>{rZAR(vat)}</span></div>}
            <div className="flex justify-between font-semibold text-navy"><span>Total</span><span>{rZAR(total)}</span></div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={() => create.mutate()} disabled={create.isPending || !form.client_name}>
              {create.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Create invoice
            </Button>
          </div>
        </section>
      )}

      <section>
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : !rows || rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No manual invoices yet. Click <strong>New invoice</strong> to raise one.
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Reference</th>
                  <th className="px-3 py-2 text-left">Client</th>
                  <th className="px-3 py-2 text-left">Issued</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r: any) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="px-3 py-2 font-mono text-xs">{r.reference}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.client_name}</div>
                      <div className="text-xs text-muted-foreground">{r.client_email}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{r.issued_date}</td>
                    <td className="px-3 py-2 text-right font-semibold">{rZAR(r.total_cents)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block rounded px-2 py-0.5 text-xs ${r.status === "paid" ? "bg-emerald-100 text-emerald-800" : r.status === "sent" ? "bg-sky-100 text-sky-800" : r.status === "cancelled" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>{r.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <Link to="/portal/admin/manual-invoices/$id" params={{ id: r.id }} className="inline-flex items-center gap-1 text-xs text-navy hover:underline">
                          <FileText className="h-3.5 w-3.5" /> Open
                        </Link>
                        <button
                          type="button"
                          onClick={() => emailMut.mutate(r)}
                          disabled={sendingId === r.id}
                          className="inline-flex items-center gap-1 text-xs text-navy hover:underline disabled:opacity-50"
                        >
                          {sendingId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Email
                        </button>
                      </div>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
